// ADR 2.10.26d §2 — the one reader and writer of the developer-tools switch,
// an account setting (roadmap 2.10.26b D2) with no local fallback.
//
// `TrainingRow`'s shape (30.9.26i §2.6): a query, an optimistic mutation,
// rollback plus `error_unknown` on failure. Two additions, because this value
// leans on the focus refetch and a gate reads it: `onMutate` cancels an
// in-flight refetch before writing the optimistic value, and a value once
// read wins over a later failed refetch. The hook never `PUT`s without a press.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { getDeveloperTools, putDeveloperTools } from '../../api/settings'
import { showSnackbar } from '../../ui/feedback'
import { developerToolsToggled } from '../admin/analytics'

const DEVELOPER_TOOLS_QUERY_KEY = ['settings', 'developer-tools'] as const

export type DeveloperToolsSurface = 'header' | 'account' | 'admin_url'

export interface DeveloperTools {
  /** `undefined` while the read is in flight; `null` when it failed. */
  shown: boolean | undefined | null
  /** Optimistic, rolled back with `error_unknown` on failure; emits §7's event on success only. */
  set(shown: boolean): void
}

/**
 * ADR 19.10.26g §5 — the switch, read only: `undefined` while the read is in
 * flight, `null` when it failed. The builder imports this and nothing else
 * from this file, so it can show the switch's one extra and never write it.
 */
export function useDeveloperToolsShown(): boolean | undefined | null {
  const read = useQuery({ queryKey: DEVELOPER_TOOLS_QUERY_KEY, queryFn: getDeveloperTools, retry: false })
  // A failed focus refetch keeps `data` and sets `isError`: the value read wins.
  return read.data ?? (read.isError ? null : undefined)
}

export function useDeveloperTools(surface: DeveloperToolsSurface): DeveloperTools {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const shown = useDeveloperToolsShown()
  const write = useMutation({
    mutationFn: putDeveloperTools,
    onMutate: async (shown: boolean) => {
      await queryClient.cancelQueries({ queryKey: DEVELOPER_TOOLS_QUERY_KEY })
      const previous = queryClient.getQueryData<boolean>(DEVELOPER_TOOLS_QUERY_KEY)
      queryClient.setQueryData(DEVELOPER_TOOLS_QUERY_KEY, shown)
      return { previous }
    },
    onSuccess: (stored) => {
      queryClient.setQueryData(DEVELOPER_TOOLS_QUERY_KEY, stored)
      developerToolsToggled(stored, surface)
    },
    onError: (_error, _shown, context) => {
      queryClient.setQueryData(DEVELOPER_TOOLS_QUERY_KEY, context?.previous)
      showSnackbar({ message: t('error_unknown') })
    },
  })
  return { shown, set: (next) => write.mutate(next) }
}
